import React, { useState, useEffect } from 'react'
import { formatCurrency, buildTeamShareUrl } from 'utils'
import { useModalBehavior, usePromotionCopy, useDiscountPreviews } from 'hooks'
import PromotionLinkModal from './PromotionLinkModal'
import VideoOrImage from '../shared/VideoOrImage'
import PackageSelector from '../shared/PackageSelector'

/**
 * CourseCategoryModal
 *
 * Usage:
 *   <CourseCategoryModal
 *     category={category}
 *     courses={allTeamCourses}
 *     colors={colors}
 *     translations={translations}
 *     trialUsed={trialUsed}
 *     onPurchase={handlePurchase}
 *     onClaimTrial={handleClaimTrial}
 *     onClose={handleClose}
 *     teamSlug={teamSlug}
 *   />
 */
export default function CourseCategoryModal({
  category,
  courses,
  colors,
  translations,
  trialUsed,
  onPurchase,
  onClaimTrial,
  onClose,
  teamSlug,
  currentUser,
  teamId,
  onConsult,
  consultationEnabled,
}) {
  const [view, setView] = useState('category') // 'category' | 'courseDetail'
  const [selectedCourseDetail, setSelectedCourseDetail] = useState(null)
  const [selectedPackages, setSelectedPackages] = useState({})

  // Note: useModalBehavior (ESC key, scroll lock) is handled by the parent (TeamCoursesSection)

  const categoryCourses = (courses || []).filter(
    (c) => category?.courseIds?.includes(c.id)
  )

  const openCourseDetail = (course) => {
    setSelectedCourseDetail(course)
    setView('courseDetail')
  }

  const handleBack = () => {
    setView('category')
    setSelectedCourseDetail(null)
  }

  const handleSelectPackage = (courseId, packageId) => {
    setSelectedPackages((prev) => ({ ...prev, [courseId]: packageId }))
  }

  const handleAction = (course) => {
    const selectedPkgId = selectedPackages[course.id]
    const trialPkg = !trialUsed ? course.packages?.find((p) => p.isTrial) : null
    const regularPkgs = course.packages?.filter((p) => !p.isTrial) || []
    const visiblePkgs = [...(trialPkg ? [trialPkg] : []), ...regularPkgs]
    const selectedPkg = visiblePkgs.find((p) => p.id === selectedPkgId)
    if (!selectedPkg) return

    if (selectedPkg.isTrial) {
      if (selectedPkg.totalPrice === 0) {
        onClaimTrial?.(course)
      } else {
        onPurchase(course, selectedPkg)
      }
    } else {
      onPurchase(course, selectedPkg)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {view === 'category' ? (
          <CategoryView
            category={category}
            categoryCourses={categoryCourses}
            colors={colors}
            translations={translations}
            trialUsed={trialUsed}
            onSelectCourse={openCourseDetail}
            onClose={onClose}
            teamSlug={teamSlug}
            currentUser={currentUser}
            teamId={teamId}
          />
        ) : (
          <CourseDetailView
            course={selectedCourseDetail}
            colors={colors}
            translations={translations}
            trialUsed={trialUsed}
            selectedPackages={selectedPackages}
            onSelectPackage={handleSelectPackage}
            onPurchase={onPurchase}
            onClaimTrial={onClaimTrial}
            onBack={handleBack}
            onClose={onClose}
            teamSlug={teamSlug}
            currentUser={currentUser}
            teamId={teamId}
            onConsult={onConsult}
            consultationEnabled={consultationEnabled}
          />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Category view
// ---------------------------------------------------------------------------

function CategoryView({ category, categoryCourses, colors, translations, trialUsed, onSelectCourse, onClose, teamSlug, currentUser, teamId }) {
  const { handleCopyClick, modalState, closeModal: closePromoModal, isCopied, inviteCode } = usePromotionCopy({ currentUser, teamId })
  const categoryShareUrl = buildTeamShareUrl(teamSlug, { category: category.id }, inviteCode)

  return (
    <>
      {/* Header */}
      <div
        className="px-6 py-4 border-b flex items-center justify-between sticky top-0 z-10"
        style={{ backgroundColor: '#E6F0FF' }}
      >
        <h2 className="text-lg font-semibold" style={{ color: '#3366FF' }}>
          {category?.name}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCopyClick(categoryShareUrl)}
            className="cursor-pointer text-gray-400 hover:text-blue-500 transition-colors p-1 rounded-lg hover:bg-blue-50"
            title={translations?.shareCategoryLink || '複製分類連結'}
          >
            {isCopied ? (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
          </button>
          <button
            onClick={onClose}
            className="cursor-pointer text-gray-400 hover:text-gray-600"
            aria-label={translations?.close || 'Close'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Category Video / Image */}
      <VideoOrImage videoUrl={category?.videoUrl} imageUrl={category?.imageUrl} alt={category?.name} />

      {/* Content */}
      <div className="p-6">
        {category?.description && (
          <p className="text-sm leading-relaxed mb-3" style={{ color: '#555' }}>
            {category.description}
          </p>
        )}
        {category?.targetAudience && (
          <p className="text-sm mb-5" style={{ color: '#717171' }}>
            {category.targetAudience}
          </p>
        )}

        {/* Course list */}
        <div className="space-y-3">
          {categoryCourses.map((course) => {
            const hasTrial = !trialUsed && !!(course.packages?.find((p) => p.isTrial))
            return (
              <div
                key={course.id}
                className="flex items-center gap-4 border border-gray-100 rounded-xl p-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
                onClick={() => onSelectCourse(course)}
              >
                {/* Thumbnail */}
                <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                  {course.imageUrl ? (
                    <img
                      src={course.imageUrl}
                      alt={course.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #EBF2FF, #DBEAFE)' }}
                    >
                      <span className="text-xs font-bold text-blue-300 text-center px-1 leading-tight">
                        {course.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: '#222' }}>
                    {course.name}
                  </p>
                  {course.targetAudience && (
                    <p className="text-xs mt-0.5 truncate" style={{ color: '#717171' }}>
                      {course.targetAudience}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {course.durationMinutes && (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {course.durationMinutes} {translations?.min || 'min'}
                      </span>
                    )}
                    {hasTrial && (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600">
                        {translations?.trialBadge || 'Trial'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <svg
                  className="w-4 h-4 shrink-0 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )
          })}
        </div>
      </div>
      <PromotionLinkModal
        isOpen={modalState.isOpen}
        onClose={closePromoModal}
        baseUrl={modalState.baseUrl}
        teamId={teamId}
        inviteCode={inviteCode}
        colors={colors}
        translations={translations?.promotionModal}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Course detail view
// ---------------------------------------------------------------------------

function CourseDetailView({
  course,
  colors,
  translations,
  trialUsed,
  selectedPackages,
  onSelectPackage,
  onPurchase,
  onClaimTrial,
  onBack,
  onClose,
  teamSlug,
  currentUser,
  teamId,
  onConsult,
  consultationEnabled,
}) {
  const { handleCopyClick, modalState, closeModal: closePromoModal, isCopied, inviteCode } = usePromotionCopy({ currentUser, teamId })
  const courseShareUrl = buildTeamShareUrl(teamSlug, { course: course?.id }, inviteCode)

  // Gift purchase state
  const isTeamMember = !!(currentUser?.teamRole)
  const [giftMode, setGiftMode] = useState(false)
  const [teamStudents, setTeamStudents] = useState([])
  const [selectedRecipientId, setSelectedRecipientId] = useState(null)
  const [loadingStudents, setLoadingStudents] = useState(false)

  useEffect(() => {
    if (!giftMode || !teamId || teamStudents.length > 0) return
    setLoadingStudents(true)
    fetch(`/api/teaching_teams/${teamId}/students?per_page=100`, {
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        const students = (data.students || []).filter(s => s.user?.id !== currentUser?.id)
        setTeamStudents(students)
      })
      .catch(() => setTeamStudents([]))
      .finally(() => setLoadingStudents(false))
  }, [giftMode, teamId])

  const trialPkg = !trialUsed ? course?.packages?.find((p) => p.isTrial) : null
  const regularPkgs = course?.packages?.filter((p) => !p.isTrial) || []
  // Hook must run before any early return (Rules of Hooks).
  const discountPreviews = useDiscountPreviews(teamId, !!currentUser?.id)

  if (!course) return null

  const visiblePkgs = [...(trialPkg ? [trialPkg] : []), ...regularPkgs]
  const selectedPkgId = selectedPackages[course.id]
  const selectedPkg = visiblePkgs.find((p) => p.id === selectedPkgId)
  const isTrialSelected = selectedPkg?.isTrial

  const buttonLabel = !selectedPkg
    ? (translations?.selectPackageHint || '← 請先選擇方案')
    : isTrialSelected
      ? (selectedPkg.totalPrice === 0
          ? (translations?.freeTrialButton || 'Free Trial')
          : `${translations?.freeTrialBuy || 'Buy Trial'} ${formatCurrency(selectedPkg.totalPrice, selectedPkg.currency)}`)
      : (translations?.purchaseButton || 'Purchase Course')

  return (
    <>
      {/* Header */}
      <div
        className="px-6 py-4 border-b flex items-center justify-between sticky top-0 z-10"
        style={{ backgroundColor: '#E6F0FF' }}
      >
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 cursor-pointer font-medium hover:opacity-70 transition-opacity"
          style={{ color: '#3366FF' }}
          aria-label={translations?.categoryBack || 'Back'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">{translations?.categoryBack || 'Back'}</span>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCopyClick(courseShareUrl)}
            className="cursor-pointer text-gray-400 hover:text-blue-500 transition-colors p-1 rounded-lg hover:bg-blue-50"
            title={translations?.shareCourseLink || '複製課程連結'}
          >
            {isCopied ? (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
          </button>
          <button
            onClick={onClose}
            className="cursor-pointer text-gray-400 hover:text-gray-600"
            aria-label={translations?.close || 'Close'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Course Video / Image */}
      <VideoOrImage videoUrl={course.videoUrl} imageUrl={course.imageUrl} alt={course.name} />

      {/* Course info */}
      <div className="p-6">
        <h3 className="text-xl font-bold mb-1" style={{ color: '#222' }}>
          {course.name}
        </h3>
        {course.targetAudience && (
          <p className="text-sm mb-3" style={{ color: '#717171' }}>
            {course.targetAudience}
          </p>
        )}
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            {course.durationMinutes} {translations?.min || 'min'}
          </span>
          {course.studentsCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              {course.studentsCount} {translations?.studentsUnit || '位學生'}
            </span>
          )}
          {course.lessonsCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {course.lessonsCount} {translations?.lessonsCompleted || '堂已完成'}
            </span>
          )}
        </div>
        {course.description && (
          <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#555' }}>
            {course.description}
          </p>
        )}
      </div>

      {/* Consultation CTA — between course content and purchase */}
      {consultationEnabled && onConsult && (
        <div className="mx-6 mb-4 rounded-xl p-4" style={{ backgroundColor: '#E6F0FF' }}>
          <p className="text-sm text-gray-700 mb-2">{translations?.courseDetailConsultTitle || '想了解更多？'}</p>
          <button
            onClick={() => {
              onClose()
              onConsult({ source: 'course_detail', courseContext: course.name })
            }}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium cursor-pointer transition-colors hover:opacity-90"
            style={{ backgroundColor: colors?.primary || '#3366FF' }}
          >
            {translations?.courseDetailConsultButton || '聯繫老師'}
          </button>
        </div>
      )}

      {/* Purchase flow */}
      {visiblePkgs.length > 0 && (
        <div className="px-6 pb-6">
          {/* Gift toggle — only visible to team members */}
          {isTeamMember && (
            <div className="border-t pt-4 mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  className={`relative w-10 h-6 rounded-full transition-colors ${giftMode ? 'bg-blue-500' : 'bg-gray-300'}`}
                  onClick={() => { setGiftMode(!giftMode); setSelectedRecipientId(null) }}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${giftMode ? 'translate-x-4' : ''}`} />
                </div>
                <span className="text-sm text-gray-700">
                  {translations?.giftToStudent || '送給學生'}
                </span>
              </label>

              {giftMode && (
                <div className="mt-3">
                  {loadingStudents ? (
                    <p className="text-sm text-gray-400">{translations?.loadingStudents || '載入學生列表...'}</p>
                  ) : teamStudents.length === 0 ? (
                    <p className="text-sm text-gray-400">{translations?.noStudents || '目前沒有學生'}</p>
                  ) : (
                    <select
                      value={selectedRecipientId || ''}
                      onChange={(e) => setSelectedRecipientId(e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{translations?.selectStudent || '選擇學生'}</option>
                      {teamStudents.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.user?.name || s.user?.email || `Student #${s.id}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={`${isTeamMember ? '' : 'border-t pt-4 '}mb-4`}>
            <p className="text-sm font-medium text-gray-700 mb-3">
              {translations?.selectPackage || '選擇方案'}
            </p>
          </div>

          <PackageSelector
            packages={visiblePkgs}
            courseDuration={course.durationMinutes}
            selectedId={selectedPkgId}
            onSelect={(pkgId) => onSelectPackage(course.id, pkgId)}
            onPurchase={(pkg) => {
              if (pkg.isTrial && pkg.totalPrice === 0) onClaimTrial?.(course)
              else if (giftMode && selectedRecipientId) {
                onPurchase(course, pkg, { recipientTeamStudentId: selectedRecipientId })
              } else {
                onPurchase(course, pkg)
              }
            }}
            trialUsed={trialUsed}
            colors={colors}
            translations={translations}
            discountPreviews={discountPreviews}
          />

        </div>
      )}
      <PromotionLinkModal
        isOpen={modalState.isOpen}
        onClose={closePromoModal}
        baseUrl={modalState.baseUrl}
        teamId={teamId}
        inviteCode={inviteCode}
        colors={colors}
        translations={translations?.promotionModal}
      />
    </>
  )
}
